import React, { useEffect, useState } from 'react'
import api from '../../../services/authService'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import { 
  IdentificationIcon, 
  EnvelopeIcon, 
  ShieldCheckIcon, 
  BuildingOffice2Icon, 
  FingerPrintIcon,
  CheckCircleIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

const PaginaPerfilAlumno: React.FC = () => {
  const { usuario } = useAuth()
  const [constitucion, setConstitucion] = useState<{ id_constitucion: number; nombre: string } | null>(null)

  useEffect(() => {
    const cargar = async () => {
      try {
        const cRes = await api.get('/api/institucion/mi/constitucion')
        setConstitucion(cRes.data)
      } catch {}
    }
    cargar()
  }, [])

  return (
    <div className="space-y-6 pb-10">
      <div className="relative overflow-hidden rounded-[24px] border border-slate-200/60 bg-white/70 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-none" />
        
        <div className="relative border-b border-slate-200/60 px-5 py-6 dark:border-zinc-800/50 md:px-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-sm border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] text-sky-700 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-sky-400">
              <IdentificationIcon className="h-4 w-4" />
              CREDENCIAL DE OPERADOR
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-zinc-100">
                Identidad Académica
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-zinc-400">
                Consulta tu nivel de autorización, base de operaciones y parámetros de conexión en la plataforma logística.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Credencial (Tarjeta Izquierda) */}
        <div className="lg:col-span-1 space-y-6">
          <div className="overflow-hidden rounded-[24px] border border-slate-200/60 bg-white/70 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000]">
            <div className="border-b border-slate-200/60 bg-slate-50/50 px-5 py-4 dark:border-zinc-800/50 dark:bg-zinc-900/30">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-zinc-100">
                <FingerPrintIcon className="h-5 w-5 text-sky-500" />
                Datos Biométricos
              </h3>
            </div>
            <div className="p-6 text-center">
              {usuario?.avatar ? (
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-white shadow-lg overflow-hidden">
                  <img src={usuario.avatar} alt="Avatar" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                </div>
              ) : (
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-sky-50 text-3xl font-bold text-sky-600 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 dark:text-sky-400">
                  {usuario?.nombre.substring(0, 2).toUpperCase() || 'OP'}
                </div>
              )}
              <h2 className="mt-4 text-lg font-bold text-slate-900 dark:text-zinc-100">
                {[usuario?.nombre, usuario?.apellido].filter(Boolean).join(' ')}
              </h2>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-zinc-500">
                ID SISTEMA: {usuario?.id || '---'}
              </p>

              <div className="mt-6 space-y-3 text-left">
                <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white p-3 shadow-sm dark:border-zinc-800/50 dark:bg-zinc-900/50">
                  <div className="rounded-lg bg-slate-50 p-2 shadow-sm dark:bg-zinc-800">
                    <EnvelopeIcon className="h-4 w-4 text-slate-400 dark:text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-zinc-500">Nodo de Contacto</p>
                    <p className="text-xs font-medium text-slate-700 dark:text-zinc-300 truncate">{usuario?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white p-3 shadow-sm dark:border-zinc-800/50 dark:bg-zinc-900/50">
                  <div className="rounded-lg bg-slate-50 p-2 shadow-sm dark:bg-zinc-800">
                    <ShieldCheckIcon className="h-4 w-4 text-slate-400 dark:text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-zinc-500">Clearance Level</p>
                    <div className="mt-0.5 flex gap-2">
                      <span className="rounded-sm border border-sky-500/20 bg-sky-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-sky-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-sky-400">
                        {usuario?.rol_organizacion || 'OPERADOR'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detalles de Operación (Derecha) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Base de Operaciones */}
          <div className="overflow-hidden rounded-[24px] border border-slate-200/60 bg-white/70 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000]">
            <div className="border-b border-slate-200/60 bg-slate-50/50 px-5 py-4 dark:border-zinc-800/50 dark:bg-zinc-900/30">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-zinc-100">
                <BuildingOffice2Icon className="h-5 w-5 text-emerald-500" />
                Base de Operaciones
              </h3>
            </div>
            <div className="p-6">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-50/50 p-5 dark:border-zinc-800/50 dark:bg-zinc-900/50">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
                  INSTITUCIÓN ASIGNADA
                </p>
                <h2 className="mt-2 text-xl font-bold text-slate-900 dark:text-zinc-100">
                  {constitucion?.nombre || 'Pendiente de Asignación'}
                </h2>
                
                <div className="mt-4 flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  <CheckCircleIcon className="h-4 w-4" />
                  Conexión segura establecida
                </div>
              </div>
            </div>
          </div>

          {/* Directivas / Acciones */}
          <div className="overflow-hidden rounded-[24px] border border-slate-200/60 bg-white/70 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000]">
            <div className="border-b border-slate-200/60 bg-slate-50/50 px-5 py-4 dark:border-zinc-800/50 dark:bg-zinc-900/30">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-zinc-100">
                <PlayCircleIcon className="h-5 w-5 text-indigo-500" />
                Directivas Operativas
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm leading-6 text-slate-600 dark:text-zinc-400 mb-6">
                Desde tu perfil operativo puedes regresar al dashboard principal para continuar con el seguimiento de tus simulaciones y operaciones en curso.
              </p>

              <div className="flex gap-4">
                <Link
                  to="/institucion/usuarios"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-sky-500"
                >
                  Regresar a la Torre
                </Link>
                <Link
                  to="/institucion/simulaciones"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200/60 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  Ver Prácticas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaginaPerfilAlumno

